import { create } from 'zustand';
import { useCallback, useMemo } from 'react';

export const useGenHistoryVisible = create((set, get) => ({
  genHistoryVisible: false,

  showHistoryModelsBar: () => set({ genHistoryVisible: true }),
  hideHistoryModelsBar: () => set({ genHistoryVisible: false }),
  toggleHistoryModelsBar: () => set({ genHistoryVisible: !get().genHistoryVisible }),
}));

/**
 * 右击菜单逻辑
 */
export const useRightMenuState = create((set) => ({
  rightMenuVisible: false,
  currentVisitItem: null,

  menuTop: 0,
  menuLeft: 0,

  showRightMenu: (visitedItem, position) => {
    set({
      rightMenuVisible: true,
      currentVisitItem: visitedItem,
      menuTop: position.top,
      menuLeft: position.left,
    });
  },

  closeRightMenu: () =>
    set({
      rightMenuVisible: false,
      currentVisitItem: null,
    }),
}));

/**
 * 查看图片详情
 */
export const useViewDetailState = create((set) => ({
  currentViewedGroupInfo: null,
  currentViewedImageIndex: 0,
  source: 'all', // all代表全部历史 album代表专辑

  viewImage: (viewImage, index, source) => {
    set({
      currentViewedGroupInfo: viewImage,
      currentViewedImageIndex: index,
      source,
    });
  },

  clearView: () =>
    set({
      currentViewedGroupInfo: null,
      currentViewedImageIndex: 0,
    }),
}));

/**
 * 大图查看
 */
export const useLargeImageViewState = create((set) => ({
  largeViewerVisible: false,
  imgs: [],

  currentViewIndex: -1,

  setLargeViewerVisible: (visible, imgs, index) =>
    set({
      largeViewerVisible: visible,
      imgs: imgs || [],
      currentViewIndex: index,
    }),

  setCurrentViewIndex: (index) =>
    set({
      currentViewIndex: index,
    }),
}));

/**
 * NSFW打开状态保留
 */
export const useNSFWStore = create(() => ({
  isNSFWView: () => {
    return false;
  },
}));

/**
 * 确认弹框
 */
export const useComfirmModalState = create((set) => ({
  modalVisible: false,
  onConfirmCallback: null,

  showComfirmModal: ({ content, subContent, onConfirmCallback }) => {
    set({
      modalVisible: true,
      content,
      subContent,
      onConfirmCallback,
    });
  },

  closeConfirmModal: () => {
    set({
      modalVisible: false,
    });
  },

  onConfirm: () => {
    set((state) => {
      if (state.onConfirmCallback) {
        state.onConfirmCallback();
      }

      return {
        modalVisible: false,
        onConfirmCallback: null,
      };
    });
  },
}));

const useInnerSelectState = create((set) => ({
  selectMode: false, // 多选状态
  seletedMap: new Map(), // 存放选中的图片id
  updateMap: (newSeleteMap) => {
    set({
      seletedMap: newSeleteMap,
    });

    if (newSeleteMap.size > 0) {
      set({
        selectMode: true, // 只有大于0时候开启选择模式，小于0不关闭选择模式
      });
    }
  },
  // 显式关闭选择模式
  clearSelectMode: () => {
    set({
      selectMode: false,
      seletedMap: new Map(),
    });
  },
}));

/**
 * 用于获得选中的图片
 */
export const useSelectDeal = () => {
  const { seletedMap } = useInnerSelectState();

  const selectedOutputId = useMemo(() => {
    const xx = seletedMap.keys();
    return Array.from(xx);
    // eslint-disable-next-line
  }, [seletedMap.size]);

  return {
    selectedOutputId,
    seletedMap,
  };
};

/**
 * 选择控制，用于统计选择总数和全选等操作
 * @returns
 */
export const useSelectRegulate = (listData) => {
  const { seletedMap, updateMap, selectMode, clearSelectMode } = useInnerSelectState();

  const selectCount = useMemo(() => {
    return seletedMap.size;
  }, [seletedMap.size]);

  /**
   * 全选
   */
  const selectAll = useCallback(() => {
    listData?.list?.forEach((item) => {
      item?.images?.forEach((i) => seletedMap.set(i.outputId, i));
    });
    updateMap(seletedMap);
  }, [seletedMap, updateMap, listData]);

  /**
   * 清除选择状态
   */
  const clearSelect = useCallback(() => {
    clearSelectMode();
  }, [clearSelectMode]);

  /**
   * 取消全选
   */
  const unselectAll = useCallback(() => {
    updateMap(new Map());
  }, [updateMap]);

  /**
   * 是否全选状态
   */
  const isSelectedAll = useMemo(() => {
    const imageCount = listData?.list?.reduce((sum, item) => {
      return sum + item?.images?.filter((i) => !i.deleted).length;
    }, 0);

    return seletedMap.size === imageCount;
  }, [seletedMap.size, listData]);

  /**
   * 是否全部收藏
   */
  const isCollectAll = useMemo(() => {
    const collectCount = listData?.list?.reduce((sum, item) => {
      return (sum += item?.images?.filter((i) => i.groupId > 0).length);
    }, 0);

    return seletedMap.size === collectCount;
  }, [seletedMap.size, listData]);

  return {
    selectCount,
    clearSelect,
    selectAll,
    unselectAll,
    selectMode,
    isSelectedAll,
    isCollectAll,
    seletedMap,
  };
};

/**
 * 选择状态，用于给单一图片使用
 */
export const useSelectState = (image) => {
  const { seletedMap, updateMap, selectMode } = useInnerSelectState();
  const { outputId: imageId } = image;

  // 某一图片是否被选中
  const isChecked = useMemo(() => {
    return seletedMap.has(imageId);
    // eslint-disable-next-line
  }, [seletedMap.size, imageId, seletedMap]);

  const seleteImage = useCallback(() => {
    if (isChecked) {
      seletedMap.delete(imageId);
    } else {
      seletedMap.set(imageId, image);
    }
    updateMap(seletedMap);
  }, [seletedMap, imageId, image, isChecked, updateMap]);

  return {
    isChecked,
    seleteImage,
    isSelectMode: selectMode,
  };
};

export const ALBUM_EVENT_TYPE_RENAME = 'rename';
export const ALBUM_EVENT_TYPE_DELETE = 'delete';

/**
 * 专辑编辑状态
 */
export const useAlbumCardState = create((set) => ({
  currentEditCard: null,
  eventType: ALBUM_EVENT_TYPE_RENAME,

  setCurrentEditCard: (editCard, eventType) =>
    set({
      currentEditCard: editCard,
      eventType,
    }),

  clearEditCard: () =>
    set({
      currentEditCard: null,
      eventType: '',
    }),
}));

/**
 * 页面滚动标志位
 */
export const usePageScrollFlag = create((set) => ({
  pageScrollFlag: false, // false不滚动，true滚动
  enableScroll: () =>
    set({
      pageScrollFlag: true,
    }),
  disableScroll: () =>
    set({
      pageScrollFlag: false,
    }),
}));
